import { Alert, Anchor, Button, Group, List, Loader, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconChevronRight, IconExternalLink, IconRefresh } from '@tabler/icons-react';
import { useState } from 'react';
import { themeEditorAppEmbedUrl } from '../../helpers/shopifyAppEmbed';
import useAppEmbedStatus from '../../hooks/useAppEmbedStatus';

const ActivateDomainAppEmbedTab = ({ domain, active, handleChangeTab }) => {
  // `active` gates the check and the poll so neither is spent while the merchant
  // is on another step: the Stepper renders only the active step's children, so
  // this is belt and braces there, but it is what makes the poll window start
  // when the merchant actually arrives.
  const { appEmbedActive, checking, check } = useAppEmbedStatus({ domain, enabled: active, poll: true });

  // Only a check the merchant asked for puts the button in its loading state.
  // Wiring it to the hook's `checking` meant the automatic mount check and the
  // 10s poll disabled the button for a slice of every cycle, so a click that
  // landed in one of those windows was swallowed and the button looked broken.
  const [checkingByHand, setCheckingByHand] = useState(false);
  const checkNow = () => {
    setCheckingByHand(true);
    check().finally(() => setCheckingByHand(false));
  };

  const confirmed = appEmbedActive === true;
  const confirmedOff = appEmbedActive === false;

  // Deliberately no effect reporting the confirmation upward on its own. Doing
  // that marked the step completed the instant the check turned green, which
  // made the wizard re-derive its current step and throw the merchant onto
  // Finished before they could read the result — and did it again if they ever
  // navigated back here, so a confirmed step was one you could not actually
  // look at. The completion is reported from the Continue button below instead,
  // which is also the only way to reach Finished, so nothing downstream can
  // read a stale value: `appEmbedActive` is carried up with it.

  return (
    <Stack>
      <Title order={3} ta="center">
        {confirmed ? 'Releva is switched on in your theme' : 'Switch Releva on in your Shopify theme'}
      </Title>
      <Text ta="center">
        Releva uses a single app embed (Releva.ai) to collect user behavior and to render web personalization elements
        in your shop. Until that embed is enabled, Releva collects nothing and shows nothing.
      </Text>

      {/* The Save step is the one merchants miss: toggling the embed on looks
          like the whole job, but Shopify discards it unless the theme is saved,
          so the shop stays untracked and the toggle they remember flipping is
          off again next time they look. */}
      {!confirmed && (
        <List type="ordered" size="sm" maw={520} mx="auto" spacing="xs">
          <List.Item>Open your Shopify theme editor with the button below.</List.Item>
          <List.Item>
            Switch the <b>Releva.ai</b> app embed on.
          </List.Item>
          <List.Item>
            Click <b>Save</b> in the top-right corner. The embed is not active until you save.
          </List.Item>
        </List>
      )}

      {confirmed && (
        <Alert color="green" icon={<IconCheck size={16} />}>
          The Releva.ai app embed is enabled. Your shop is being tracked.
        </Alert>
      )}
      {confirmedOff && (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
          The Releva.ai app embed is not enabled yet. Follow the three steps above — including <b>Save</b>, without
          which Shopify discards the change.
        </Alert>
      )}
      {/* Nothing has answered yet. While the first check is still in flight that
          is simply "we are looking", not a finding, so it must not be dressed up
          as one — a revisit to an already-enabled step would otherwise open on a
          warning about the very thing it is about to confirm. */}
      {appEmbedActive === null &&
        (checking ? (
          <Group justify="center" gap="xs">
            <Loader size="xs" />
            <Text c="dimmed" size="sm">
              Checking your theme…
            </Text>
          </Group>
        ) : (
          <Alert color="gray" icon={<IconAlertTriangle size={16} />}>
            We cannot confirm whether the Releva.ai app embed is enabled right now. Open your theme editor to turn it
            on, or check again in a moment.
          </Alert>
        ))}

      {confirmed ? (
        <Group justify="flex-end" mt="md">
          <Button
            component="a"
            variant="default"
            href={themeEditorAppEmbedUrl(domain)}
            target="_blank"
            rel="noreferrer"
            rightSection={<IconExternalLink size={14} />}
          >
            Open the theme editor
          </Button>
          <Button
            onClick={() => handleChangeTab({ name: 'appEmbed', status: 'completed', appEmbedActive: true })}
            rightSection={<IconChevronRight size={14} />}
          >
            Next
          </Button>
        </Group>
      ) : (
        <Group justify="center">
          <Button
            component="a"
            href={themeEditorAppEmbedUrl(domain)}
            target="_blank"
            rel="noreferrer"
            rightSection={<IconExternalLink size={14} />}
          >
            Enable the Releva app embed
          </Button>
          <Button variant="default" onClick={checkNow} loading={checkingByHand} leftSection={<IconRefresh size={14} />}>
            I&apos;ve activated it — check now
          </Button>
        </Group>
      )}

      {!confirmed && (
        <>
          <Group justify="center">
            <Anchor
              component="button"
              type="button"
              c="dimmed"
              onClick={() =>
                // Hand over what a check actually answered, which is null if none
                // has, so a skip taken before or despite an answer is never
                // reported as a confirmed off.
                handleChangeTab({ name: 'appEmbed', status: 'skipped', appEmbedActive })
              }
            >
              I&apos;ll do this later
            </Anchor>
          </Group>
          <Text ta="center" c="dimmed" size="sm">
            If you skip this step, Releva will not track your visitors and will not personalize your shop until the app
            embed is enabled. You can come back to it from the alert on your dashboard.
          </Text>
        </>
      )}
    </Stack>
  );
};

export default ActivateDomainAppEmbedTab;
